import cn from "classnames";
import { isEmpty, once } from "lodash";
import React, { Component, createRef, type MutableRefObject } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "redux";

import NotifyStep from "../commonSteps/notifyStep/NotifyStep";
import { type ModalActionConfig, type RenderActions, type RenderModalActionsType, type StepsOptions } from "../types";
import { renderSteps } from "../utils/renderSteps";

import { AssignmentPeopleContext, CommunicationChannels, Strings, TemplateTypes } from "../../../enums";
import { type AppDispatch, type RootState } from "../../../features/Application/globaltypes/redux";
import { bindAction, type IObservable, type NotifyStepSettings, type PriorityLevel } from "../../../interfaces";
import { type GenericFiltersMap } from "../../../utils/filterUtils";
import { type Filters } from "../../../utils/queryUtils";
import ConfirmLicensingStep from "../commonSteps/confirmLicensingStep/ConfirmLicensingStep";
import PriorityLevelsStep from "../commonSteps/userAssignmentSteps/priorityLevelsStep/PriorityLevelsStep";
import { ContentListStep } from "./contentAssignmentSteps";

import * as addToContentActionsRedux from "../../../features/People/state/slices/addToContentSlice";

import SortOptions from "../../../enums/SortOptions";
import userListUtils from "../../../utils/userListUtils";
import ModalWithSteps from "../../modal/ModalWithSteps";

import { Button } from "components/buttons/button/Button";
import { FeatureFlags } from "featureFlags";
import { ContentType, type PeopleType } from "features/Library/PeopleAssignments/types";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { type LDProps } from "../../../features/LDProps";
import { resetContentPacksAction } from "../../../features/Licensing/ContentAssignmentModalLicensingSteps/state/thunks/assignmentModalLicensingThunk";
import { type PeopleAvailableFlow } from "../../../features/People/ContentAssignments/models";
import { reset } from "../../../features/People/ContentAssignments/state/slices/contentAssignmentsSlice";
import { fetchPeopleAvailableItems } from "../../../features/People/ContentAssignments/state/thunks/contentAssignmentsThunk";
import {
  fetchContentAssignmentsFilterOptions,
  resetContentAssignmentsFilterOptions,
} from "../../../features/People/ContentAssignments/state/thunks/contentAssignmentsFilterThunk";
import { type PriorityItem } from "../../../features/People/types";
import { initialNotifyConfigDefault } from "../../../features/SystemNotifications/config";
import {
  withNotifyConfig,
  type WithNotifyConfigProps,
} from "../../../features/SystemNotifications/containers/withNotifyConfig";
import { resetState } from "../../../features/SystemNotifications/state/slices/notifyStepSlice";
import { type NotifySettings } from "../../../features/SystemNotifications/types";
import { NotifyStepSwitch } from "../../notifyStep/NotifyStepSwitch";
import SearchInput from "../../searchInput/SearchInput";
import PacksContextStep from "../commonSteps/packsContextStep/PacksContextStep";
import "./contentAssignmentModal.scss";
import { renderModalActionsFirstStep, renderModalActionsMiddleStep } from "./utils/contentAssignmentModalActions";
import noop from "lodash/noop";
import { hasFlowViewPermissionSelector } from "../../../features/People/state/selectors";
import { updateLicensePackInfo } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/licenseConfirmationInfoSlice";
import { type LicenseConfirmationFormRef } from "features/Licensing/ContentAssignmentModalLicensingSteps/LicenseConfirmationModalContent/LicenseConfirmationBlockV2/LicenseConfirmationBlock";
import PaymentModal from "features/Licensing/Packs/PaymentModal/PaymentModal";
import { Observable } from "utils";
import NotificationModal, { type NotificationModalState } from "components/modal/NotificationModal/NotificationModal";
import { SwitchEntityTypes } from "enums/switchEntityTypes";

type PropsFromRedux = ConnectedProps<typeof connector>;

interface ContentAssignmentState {
  selectedIds: number[];
  appliedFilter: Filters;
  sortOptions: string;
  isDataValid: boolean;
  isNotifyTabValid: boolean;
  isDataLoaded: boolean;
  skipPacksContextStep: boolean;
  packsContextStepHeader?: string;
  search: string;
  isPaymentModalOpen: boolean;
}

export type ContentAssignmentModalProps = PropsFromRedux & {
  peopleType: PeopleType;
  onConfirm?: (notificationSettings?: NotifyStepSettings) => void;
  onClose?: () => void;
  notifyTemplateType?: TemplateTypes;
};
export type Props = ContentAssignmentModalProps & WithNotifyConfigProps<NotifySettings> & LDProps;

const initialState: ContentAssignmentState = {
  selectedIds: [],
  sortOptions: SortOptions.ModifiedDateDesc,
  appliedFilter: {},
  isDataValid: true,
  isNotifyTabValid: false,
  isDataLoaded: false,
  skipPacksContextStep: false,
  packsContextStepHeader: undefined,
  search: "",
  isPaymentModalOpen: false,
};

const INITIAL_ITEM_SKIP = 0;

export class ContentAssignmentModal extends Component<Props, ContentAssignmentState> {
  private readonly onPreviousNotifyStepRef: React.MutableRefObject<() => Promise<void>>;
  private readonly getNotifySettingsRef: React.MutableRefObject<() => Promise<NotifyStepSettings | undefined>>;
  private readonly flexiblePriorityDueDate: boolean;
  private readonly additionalPurchaseEnabled: boolean;
  private readonly licenseConfirmationFormRef: MutableRefObject<LicenseConfirmationFormRef>;
  private readonly onTriggerNotificationModalObserver: IObservable<
    (onConfirm: () => void, entityState: NotificationModalState) => void
  >;

  constructor(props: Props) {
    super(props);

    this.onPreviousNotifyStepRef = createRef() as MutableRefObject<() => Promise<void>>;
    this.getNotifySettingsRef = createRef() as MutableRefObject<() => Promise<NotifyStepSettings | undefined>>;
    this.state = initialState;
    this.flexiblePriorityDueDate = !!this.props.flags?.[FeatureFlags.FlexiblePriorityDueDate];
    this.additionalPurchaseEnabled = !!this.props.flags?.[FeatureFlags.AdditionalPurchase];
    this.licenseConfirmationFormRef = createRef() as MutableRefObject<LicenseConfirmationFormRef>;
    this.onTriggerNotificationModalObserver = new Observable();
  }

  componentDidMount() {
    const { filterActions, hasAnyPermission } = this.props;

    if (hasAnyPermission) {
      this.fetchAvailableItems();
      filterActions.fetchFilterOptions();
    }
  }

  componentWillUnmount() {
    const { availableItemsActions, filterActions } = this.props;
    filterActions.resetFilter();
    availableItemsActions.resetGrid();
    this.props.addToContentActions.resetModal();
    this.props.resetNotificationState();
    this.onIsDataLoaded(false);
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.showModal !== this.props.showModal && !this.props.showModal) {
      this.props.notifyConfigPayload.resetNotifyConfig();
    }
  }

  onSelectedItemsChanged = (ids: number[]) => {
    const { items, addToContentActions, priorityItems } = this.props;
    const payloadContentType = ContentType.Flow;
    this.setState({ selectedIds: ids }, () => {
      const filteredPriorityItems = priorityItems.filter(
        (x) => x.blockType !== payloadContentType || ids.includes(x.id),
      );
      const priorityItemsToAdd = ids
        .filter((id) => !filteredPriorityItems.some((x) => x.id === id))
        .map((id) => {
          const selected = items.find((item) => item.id === id);
          return { ...userListUtils.mapToPriorityItem(selected, payloadContentType) };
        });

      addToContentActions.setPriorityItems([...filteredPriorityItems, ...priorityItemsToAdd]);
      this.setSkipPacksContextStep(false);
      this.props.resetContentPacks();
    });
  };

  fetchAvailableItems = (skip?: number) => {
    const { availableItemsActions, selectedUserIds, selectedPeopleType } = this.props;
    const { sortOptions, appliedFilter, search } = this.state;

    availableItemsActions.fetchAvailableItems(
      skip ?? INITIAL_ITEM_SKIP,
      sortOptions,
      { ...appliedFilter, isDraft: false },
      selectedUserIds,
      selectedPeopleType,
      search,
    );
  };

  onSortOptionsChanged = (_: React.SyntheticEvent<HTMLElement>, data: { [key: string]: any }) => {
    this.onSelectedItemsChanged([]);
    this.props.availableItemsActions.resetGrid();
    this.setState({ sortOptions: data.value }, this.loadItems);
  };

  onCloseModal = () => {
    this.props.resetContentPacks();
    this.props.onClose?.();
    this.props.addToContentActions.resetModal();
  };

  resetFilter = async () => {
    this.onSelectedItemsChanged([]);

    // await prevents too early new request calling (loadItemsToAddPage) because of the wrong skip parameter increment
    await this.props.availableItemsActions.resetGrid(); // NOSONAR
    this.setState({ appliedFilter: {} }, this.loadItemsToAddPage);
  };

  applyFilter = (filter: Filters) => {
    const { availableItemsActions } = this.props;
    this.onSelectedItemsChanged([]);
    availableItemsActions.resetGrid();
    this.setState({ appliedFilter: filter }, () => this.fetchAvailableItems());
  };

  applySearch = (search: string) => {
    const { availableItemsActions } = this.props;
    this.setState(
      (prevState) => {
        let sortOptions = prevState.sortOptions;
        const searchWasCleared = prevState.search && !search;

        if (searchWasCleared) {
          sortOptions = initialState.sortOptions;
        } else if (search) {
          sortOptions = "";
        }

        return {
          sortOptions,
          search,
        };
      },
      () => {
        availableItemsActions.resetGrid();
        this.fetchAvailableItems();
      },
    );
  };

  loadItemsToAddPage = () => {
    this.fetchAvailableItems(this.props.items.length);
  };

  loadItems = () => {
    this.fetchAvailableItems();
  };

  onPriorityItemsUpdate = (priorityLevel: PriorityLevel[]) => {
    this.props.addToContentActions.setPriorityItems(priorityLevel as PriorityItem[]);
  };

  onIsDataValidChange = (isDataValid: boolean) => {
    if (this.state.isDataValid !== isDataValid) {
      this.setState({ isDataValid });
    }
  };

  onNotifyTabValidChange = (isValid: boolean) => {
    this.setState({ isNotifyTabValid: isValid });
  };

  onIsDataLoaded = (isDataLoaded: boolean) => {
    if (this.state.isDataLoaded !== isDataLoaded) {
      this.setState({ isDataLoaded });
    }
  };

  setSkipPacksContextStep = (skipPacksContextStep: boolean) => {
    this.setState({ skipPacksContextStep });
  };

  onResetNotifyStep = () => {
    this.props.resetNotificationState();
  };

  setPacksContextStepHeader = (packsContextStepHeader?: string) => {
    if (this.state.packsContextStepHeader !== packsContextStepHeader) {
      this.setState({ packsContextStepHeader });
    }
  };

  confirm = (notificationSettings?: NotifyStepSettings) => {
    this.props.onConfirm?.();
    this.props.addToContentActions.onSubmitModal(this.flexiblePriorityDueDate, notificationSettings);
    this.props.resetContentPacks();
  };

  renderModalActionsLastStep =
    ({ previous, confirm, next }: ModalActionConfig) =>
    (_: () => void, prevStep: () => void) =>
    (closeModal: Function) => {
      const confirmHandler = async () => {
        if (this.additionalPurchaseEnabled && this.props.isPurchaseNeeded) {
          this.props.updateLicensePackInfoAction(this.licenseConfirmationFormRef.current?.getValues());
          this.setState({ isPaymentModalOpen: true });
        } else {
          const onClickResult = await confirm?.onClick?.();
          this.confirm(onClickResult);
        }
        closeModal();
      };
      return (
        <>
          <Button
            blur
            primary
            className="previous"
            content="Previous"
            onClick={async () => {
              await previous?.onClick?.();
              prevStep();
            }}
          />
          <Button
            primary
            className="confirm"
            content={next?.content || "Finish"}
            onClick={once(confirmHandler)}
            disabled={confirm?.disabled}
          />
        </>
      );
    };

  renderContentListStep = (renderModalActions: RenderModalActionsType) => {
    const {
      items,
      itemsCount,
      isLoading,
      isAllLoaded,
      filterOptions,
      selectedUserIds,
      contentType,
      hasAnyPermission,
      selectedPeopleType,
    } = this.props;
    const { selectedIds, appliedFilter, sortOptions } = this.state;

    return (
      <ContentListStep
        blur
        key="Add Content"
        renderSearch={() => (
          <SearchInput placeholder="Search..." onChange={this.applySearch} defaultValue={this.state.search} />
        )}
        searchValue={this.state.search}
        orderBy={sortOptions}
        header={Strings.modalTitles.addContent}
        contentType={contentType}
        renderModalActions={renderModalActions}
        loadData={this.loadItemsToAddPage}
        items={items.map((item: PeopleAvailableFlow) => ({
          ...item,
          usersCount: selectedUserIds.length,
          selectedPeopleType,
        }))}
        itemsCount={itemsCount}
        isLoading={isLoading}
        isAllLoaded={isAllLoaded}
        filter={appliedFilter}
        filterOptions={filterOptions}
        applyFilter={this.applyFilter}
        resetFilter={this.resetFilter}
        selectedIds={selectedIds}
        onSelectedItemsChanged={this.onSelectedItemsChanged}
        onSortOptionsChanged={this.onSortOptionsChanged}
        accessRestricted={!hasAnyPermission}
        onContentSegmentationChange={noop}
        selectedPeopleType={selectedPeopleType}
      />
    );
  };

  renderPriorityLevelsStep = (renderModalActions: RenderModalActionsType) => (
    <PriorityLevelsStep
      peopleType={this.props.peopleType}
      key="Priority Settings"
      header="Priority Settings"
      renderModalActions={renderModalActions}
      priorityItems={this.props.priorityItems}
      onItemsUpdate={this.onPriorityItemsUpdate}
      onIsDataValidChange={this.onIsDataValidChange}
    />
  );

  renderPacksContextStep = (renderModalActions: RenderModalActionsType) => {
    const { packsContextStepHeader } = this.state;

    return (
      <PacksContextStep
        key="Licensing"
        header={packsContextStepHeader}
        renderModalActions={renderModalActions}
        info={this.props.priorityItems.map((i) => {
          return { ...i, id: i.id.toString(), type: i.blockType };
        })}
        onIsDataValidChange={this.onIsDataValidChange}
        noDataLoaded={() => this.setSkipPacksContextStep(true)}
        setModalStepHeader={() => this.setPacksContextStepHeader("Licensing")}
      />
    );
  };

  renderConfirmLicensingStep = (renderModalActions: RenderModalActionsType) => {
    const { selectedUserIds } = this.props;

    return (
      <ConfirmLicensingStep
        key="License Confirmation"
        header="License Confirmation"
        renderModalActions={renderModalActions}
        info={{
          userIds: this.props.selectedPeopleType === AssignmentPeopleContext.User ? selectedUserIds : [],
          groupIds: this.props.selectedPeopleType === AssignmentPeopleContext.Group ? selectedUserIds : [],
        }}
        onIsDataValidChange={this.onIsDataValidChange}
        licenseConfirmationFormRef={this.licenseConfirmationFormRef}
      />
    );
  };

  renderNotifyStep = (renderModalActions: RenderModalActionsType) => {
    const {
      notifyConfig,
      notifyConfigPayload: { setNotifyConfig, shouldNotify, communicationChannel },
    } = this.props;

    return (
      <NotifyStep
        preRender
        key="Notify"
        header="Notify"
        renderModalActions={renderModalActions}
        onIsDataValidChange={this.onNotifyTabValidChange}
        onIsDataLoaded={this.onIsDataLoaded}
        isDataLoaded={this.state.isDataLoaded}
        onPreviousNotifyStepRef={this.onPreviousNotifyStepRef}
        getNotifySettingsRef={this.getNotifySettingsRef}
        notifyTemplateType={TemplateTypes.AddedToFlowV2}
        renderSwitch={(switchProps) => (
          <NotifyStepSwitch config={notifyConfig} onNotifyConfigChange={setNotifyConfig} switchProps={switchProps} />
        )}
        shouldNotify={shouldNotify}
        communicationChannel={communicationChannel}
      />
    );
  };

  getStepsOptions = (): StepsOptions[] => {
    const { selectedIds, isDataValid, isNotifyTabValid, skipPacksContextStep, isDataLoaded } = this.state;
    const {
      addToContentActions,
      selectedUserIds,
      isLoading,
      resetContentPacks,
      priorityItems,
      notifyConfigPayload: { shouldNotify, communicationChannel },
    } = this.props;
    const notifyStepNotReady =
      communicationChannel !== CommunicationChannels.MsTeams && (!isDataLoaded || !isNotifyTabValid);
    const initialBlockType = priorityItems?.[0]?.blockType;
    const skipNotifyContextStep = priorityItems.some(
      ({ hasBeenPublished, blockType }) => !hasBeenPublished || blockType !== initialBlockType,
    );
    return [
      {
        renderStep: this.renderContentListStep,
        modalActionConfig: {
          previous: {
            onClick: () => {
              resetContentPacks();
              addToContentActions.resetModal();
            },
          },
          next: {
            disabled: isEmpty(selectedIds) || isEmpty(selectedUserIds) || isLoading,
            content: "Add",
          },
        },
      },
      {
        renderStep: this.renderPriorityLevelsStep,
        modalActionConfig: {
          previous: {
            disabled: !isDataValid,
            onClick: () => {
              this.onIsDataValidChange(true);
              this.props.addToContentActions.resetPriorityItems();
            },
          },
          next: {
            disabled: !isDataValid,
            onClick: () => this.onIsDataValidChange(false),
          },
        },
      },
      {
        renderStep: this.renderPacksContextStep,
        skipStep: skipPacksContextStep,
        modalActionConfig: {
          previous: {
            onClick: () => this.onIsDataValidChange(true),
          },
          next: {
            disabled: !isDataValid,
            onClick: () => this.onIsDataValidChange(false),
          },
        },
      },
      {
        renderStep: this.renderConfirmLicensingStep,
        modalActionConfig: {
          previous: {
            onClick: () => this.onIsDataValidChange(true),
          },
          next: {
            disabled: !isDataValid,
            content: this.additionalPurchaseEnabled && this.props.isPurchaseNeeded ? "Proceed to Payment" : undefined,
            onClick: () => {
              this.onIsDataValidChange(false);
            },
          },
          confirm: {
            disabled: !isDataValid,
          },
        },
      },
      {
        renderStep: this.renderNotifyStep,
        skipStep: skipNotifyContextStep || (this.props.isPurchaseNeeded && this.additionalPurchaseEnabled),
        modalActionConfig: {
          previous: {
            onClick: () => this.onPreviousNotifyStepRef?.current?.(),
          },
          confirm: {
            disabled: shouldNotify && notifyStepNotReady,
            onClick: () => {
              return this.getNotifySettingsRef?.current?.();
            },
          },
        },
      },
    ];
  };

  renderActions: RenderActions = {
    renderModalActionsFirstStep,
    renderModalActionsMiddleStep,
    renderModalActionsLastStep: this.renderModalActionsLastStep,
  };

  onPaymentModalClose = () => {
    this.setState({ isPaymentModalOpen: false });
    this.onCloseModal();
  };

  onPaymentSuccess = () => {
    this.setState({ isPaymentModalOpen: false }, () => {
      const entityState: NotificationModalState = {
        entityType: SwitchEntityTypes.Flow,
        templateType: this.props.notifyTemplateType ?? TemplateTypes.AddedToFlowV2,
      };
      this.onTriggerNotificationModalObserver.notify(noop, entityState);
    });
  };

  render() {
    const { showModal, hasAnyPermission } = this.props;

    return (
      <>
        <ModalWithSteps
          className={cn("content-assignment-modal", { restricted: !hasAnyPermission })}
          scrolling
          showModal={showModal}
          onCancel={this.onCloseModal}
          onBeforeClose={this.onResetNotifyStep}
        >
          {renderSteps(this.getStepsOptions(), this.renderActions)}
        </ModalWithSteps>
        {this.state.isPaymentModalOpen && this.props.paymentPacks && (
          <PaymentModal
            paymentPacks={this.props.paymentPacks}
            onClose={this.onPaymentModalClose}
            onPaymentSuccess={this.onPaymentSuccess}
          />
        )}
        {this.additionalPurchaseEnabled && (
          <NotificationModal
            onTriggerPublishContentObserver={this.onTriggerNotificationModalObserver}
            onConfirmAction={(notificationConfirmPayload) =>
              this.confirm(notificationConfirmPayload.notificationSettings)
            }
            onClose={this.onCloseModal}
          />
        )}
      </>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  items: state.people.contentToPeople.assignments.items,
  isLoading: state.people.contentToPeople.assignments.isLoading,
  isAllLoaded: state.people.contentToPeople.assignments.areAllLoaded,
  itemsCount: state.people.contentToPeople.assignments.itemsCount,
  filterOptions: state.people.contentToPeople.filters.filterOptions,
  selectedUserIds: state.people.addToContentModal.selectedPeopleIds,
  contentType: state.people.addToContentModal.selectedContentType,
  showModal: state.people.addToContentModal.showModal,
  priorityItems: state.people.addToContentModal.priorityItems,
  selectedPeopleType: state.people.addToContentModal.selectedPeopleType,
  userPermissions: state.userProfile.permissions,
  hasAnyPermission: hasFlowViewPermissionSelector(state),
  isPurchaseNeeded: state.licensing?.availableLicenses?.isPurchaseNeeded,
  paymentPacks: state.licensing.licenseConfirmationInfo.paymentPacks,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    filterActions: {
      resetFilter: () => dispatch(resetContentAssignmentsFilterOptions()),
      fetchFilterOptions: () => dispatch(fetchContentAssignmentsFilterOptions()),
    },
    availableItemsActions: {
      resetGrid: () => dispatch(reset()),
      fetchAvailableItems: <TFilters extends string | number>(
        skip: number,
        orderBy: string,
        filter: GenericFiltersMap<TFilters>,
        selectedUserIds: number[],
        modalType: AssignmentPeopleContext,
        search?: string,
      ) =>
        dispatch(
          fetchPeopleAvailableItems(
            { userIds: selectedUserIds, skip, orderBy, filterParams: filter, search },
            modalType,
          ),
        ),
    },
    addToContentActions: bindActionCreators(addToContentActionsRedux, dispatch),
    resetContentPacks: bindActionCreators(resetContentPacksAction, dispatch),
    resetNotificationState: bindAction(resetState, dispatch),
    updateLicensePackInfoAction: bindAction(updateLicensePackInfo, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const ConnectedComponent = connector(
  withNotifyConfig(withLDConsumer()(ContentAssignmentModal), initialNotifyConfigDefault),
);
export default ConnectedComponent;
